import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { trackEvent } from '../lib/metaPixel'
import TurnstileWidget from '../components/TurnstileWidget'

export default function Login() {
  const { user, loading: authLoading } = useAuth()
  const [modo, setModo] = useState(
    typeof window !== 'undefined' && window.location.pathname === '/suscribirme'
      ? 'signup'
      : 'login'
  )   // 'login' | 'signup' — /suscribirme defaultea a signup (post apertura beta); ?modo= sigue override
  const [email, setEmail] = useState('')
  const [emailConfirm, setEmailConfirm] = useState('')  // signup: rechequeo email para evitar typos
  const [password, setPassword] = useState('')
  const [nombre, setNombre] = useState('')
  const [apellido, setApellido] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState(null)
  const [signupOk, setSignupOk] = useState(false)   // Mensaje "Confirmá tu email"
  const [turnstileToken, setTurnstileToken] = useState(null)  // anti-bot signup
  const [betaCerrada, setBetaCerrada] = useState(true)  // Default seguro: cerrada hasta que sepamos
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // P1 fix (2026-05-15): validar que `next` sea path relativo seguro.
  // Aceptamos solo strings que empiecen con '/' y NO con '//' (protocol-relative
  // que permitiria redirect a otro dominio, ej. //evil.com).
  const rawNext = params.get('next')
  const next = (rawNext && typeof rawNext === 'string' && rawNext.startsWith('/') && !rawNext.startsWith('//'))
    ? rawNext
    : null
  const initialModo = params.get('modo')

  // Solo permitir signup si la beta está abierta
  useEffect(() => {
    if (initialModo === 'signup' && !betaCerrada) setModo('signup')
  }, [initialModo, betaCerrada])

  // Leer flag global de beta_cerrada desde app_config
  useEffect(() => {
    let cancel = false
    ;(async () => {
      const { data } = await supabase
        .from('app_config')
        .select('value')
        .eq('key', 'beta_cerrada')
        .maybeSingle()
      if (cancel) return
      const cerrada = data?.value === 'true' || data?.value === true || data?.value === '"true"'
      setBetaCerrada(cerrada)
      // Si está cerrada y modo=signup, forzar a login
      if (cerrada) setModo('login')
    })()
    return () => { cancel = true }
  }, [])

  // Si ya hay sesión activa → redirigir
  useEffect(() => {
    if (!authLoading && user) {
      navigate(next || '/', { replace: true })
    }
  }, [user, authLoading, next, navigate])

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    if (modo === 'signup') {
      if (!turnstileToken) {
        setError('Completá la verificación anti-bot antes de continuar')
        setLoading(false)
        return
      }
      // Validaciones cliente (nombre + apellido + email + confirmación email)
      if (!nombre.trim() || !apellido.trim()) {
        setError('Ingresá nombre y apellido')
        setLoading(false)
        return
      }
      if (email.trim().toLowerCase() !== emailConfirm.trim().toLowerCase()) {
        setError('Los emails no coinciden — revisá que estén escritos igual')
        setLoading(false)
        return
      }
      // Signup en 2 pasos (14-jul-2026): 1) magic-link crea la sesión, 2) /activar-cuenta pide contraseña.
      // Baja fricción del form inicial (sin password/CI) pero mantiene percepción de seguridad al final.
      // Cédula + teléfono se piden después, en el checkout del kit.
      const nextParam = next ? `?next=${encodeURIComponent(next)}` : ''
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: {
          shouldCreateUser: true,
          data: { nombre: nombre.trim(), apellido: apellido.trim() },
          // El link del mail lleva a /activar-cuenta donde el user elige su contraseña.
          emailRedirectTo: `https://panel.limpack.uy/activar-cuenta${nextParam}`,
          captchaToken: turnstileToken,
        },
      })
      if (error) {
        setError(traducirError(error.message))
        setLoading(false)
        return
      }
      // Meta Pixel: Lead = signup completado (paso 1 del flow, todavía sin password)
      trackEvent('Lead', { content_name: 'signup_email_step1' })
      setSignupOk(true)
      setLoading(false)
      return
    }
    // Login normal
    if (!turnstileToken) {
      setError('Completá la verificación anti-bot antes de continuar')
      setLoading(false)
      return
    }
    const { error } = await supabase.auth.signInWithPassword({
      email, password,
      options: { captchaToken: turnstileToken },
    })
    if (error) {
      setError(traducirError(error.message))
      setLoading(false)
      return
    }
    navigate(next || '/', { replace: true })
  }

  async function handleGoogle() {
    setError(null)
    setGoogleLoading(true)
    // Fix seguridad 7-jul-2026: prompt=select_account fuerza a Google a mostrar
    // el selector de cuenta cada vez (aunque haya sesión activa). Previene que en PC pública
    // el próximo user entre auto a la cuenta anterior con solo tocar "Continuar con Google".
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}${next || '/'}`,
        queryParams: {
          prompt: 'select_account',
          access_type: 'offline',
        },
      },
    })
    if (error) {
      setError(error.message)
      setGoogleLoading(false)
    }
  }

  return (
    <div className="lp-login">
      {/* Navbar simple igual que la web pública */}
      <nav className="lp-login-nav">
        <div className="lp-login-nav-inner">
          <a href="https://limpack.uy" className="lp-login-logo">Lim<span>pack</span></a>
          {betaCerrada ? (
            <span className="lp-login-nav-link" style={{ color: '#fff', fontSize: 13, opacity: 0.85 }}>
              🔒 Beta cerrada
            </span>
          ) : modo === 'login' ? (
            <button type="button" className="lp-login-nav-link" onClick={() => { setModo('signup'); setError(null) }} style={{ background: 'transparent', border: 0, cursor: 'pointer', font: 'inherit', color: '#fff' }}>
              ¿No tenés cuenta? <strong>Crear cuenta</strong>
            </button>
          ) : (
            <button type="button" className="lp-login-nav-link" onClick={() => { setModo('login'); setError(null); setSignupOk(false) }} style={{ background: 'transparent', border: 0, cursor: 'pointer', font: 'inherit', color: '#fff' }}>
              ¿Ya tenés cuenta? <strong>Entrar</strong>
            </button>
          )}
        </div>
      </nav>

      <main className="lp-login-main">
        <div className="lp-login-card">
          {/* Isotipo Limpack (la gota) */}
          <div className="lp-login-iso">
            <img src="/isotipo.png" alt="Limpack" />
          </div>

          <div className="lp-login-head">
            {signupOk ? (
              <>
                <h1>📧 Revisá tu email</h1>
                <p>Te mandamos un link a <strong>{email}</strong> para confirmar tu cuenta. Hacé click ahí y volvé.</p>
              </>
            ) : modo === 'signup' ? (
              <>
                <h1>Crear cuenta</h1>
                <p>Sumate a Limpack en 30 segundos. Después vas a elegir tu contraseña.</p>
              </>
            ) : (
              <>
                <h1>Ingresá a tu cuenta</h1>
                <p>{betaCerrada ? 'Beta cerrada — solo invitados.' : 'Clientes, porteros y equipo Limpack.'}</p>
              </>
            )}
          </div>

          {params.get('baja') === 'ok' && (
            <div style={{ padding: 14, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, color: '#15803d', fontSize: 13, lineHeight: 1.55, marginBottom: 16 }}>
              <strong>✓ Tu cuenta fue dada de baja.</strong> Esperamos volver a verte. Si fue un error o querés reactivar tu cuenta, escribinos al{' '}
              <a href="https://wa.me/59898610001" target="_blank" rel="noopener" style={{ color: '#15803d', fontWeight: 700 }}>WhatsApp 098 610 001</a>.
            </div>
          )}

          {/* Fix seguridad 7-jul-2026: aviso post-logout para PC pública */}
          {params.get('logout') === '1' && (
            <div style={{ padding: 14, background: '#fff8e6', border: '1px solid #fbbf24', borderRadius: 10, color: '#78350f', fontSize: 13, lineHeight: 1.55, marginBottom: 16 }}>
              <strong>🔒 Sesión cerrada en Limpack.</strong><br/>
              Si estás en una computadora pública, cerrá también tu sesión de Google en{' '}
              <a href="https://accounts.google.com/logout" target="_blank" rel="noopener" style={{ color: '#78350f', fontWeight: 700 }}>accounts.google.com/logout</a>{' '}
              para que nadie entre a tu cuenta después.
            </div>
          )}

          {betaCerrada && !signupOk && (
            <div style={{ padding: 14, background: '#fff8e6', border: '1px solid #fcd34d', borderRadius: 10, color: '#78350f', fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
              <strong>🔒 Estamos en beta cerrada.</strong><br/>
              El registro está restringido a usuarios invitados. Si querés ser parte, escribinos al{' '}
              <a href="https://wa.me/59898610001" target="_blank" rel="noopener" style={{ color: '#78350f', fontWeight: 700 }}>WhatsApp 098 610 001</a>.
            </div>
          )}

          {signupOk && (
            <div style={{ padding: 20, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, color: '#15803d', fontSize: 14, lineHeight: 1.5, textAlign: 'center', marginTop: 8 }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>✉️</div>
              <strong style={{ fontSize: 15 }}>Revisá tu email</strong>
              <div style={{ marginTop: 6 }}>
                Te mandamos un link a <strong>{email}</strong>.<br/>
                Click en ese link y elegís tu contraseña para activar la cuenta.
              </div>
              <div style={{ marginTop: 14, fontSize: 12, color: '#15803d' }}>
                ¿No te llegó en 2 min? Revisá spam o{' '}
                <button type="button" onClick={() => { setSignupOk(false); setEmailConfirm('') }} style={{ background: 'transparent', border: 0, color: '#15803d', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                  intentá de nuevo
                </button>
              </div>
            </div>
          )}

          {!signupOk && (<>
          {/* Google OAuth */}
          <button
            type="button"
            onClick={handleGoogle}
            disabled={googleLoading}
            className="lp-btn-google"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
              <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            {googleLoading ? 'Abriendo Google…' : 'Continuar con Google'}
          </button>

          <div className="lp-divider"><span>o con email</span></div>

          <form onSubmit={handleSubmit} className="lp-login-form">
            {modo === 'signup' && (
              <>
                <label className="lp-login-field">
                  <b>Nombre</b>
                  <input
                    type="text"
                    required
                    autoComplete="given-name"
                    value={nombre}
                    onChange={e => setNombre(e.target.value)}
                    placeholder="Juan"
                  />
                </label>
                <label className="lp-login-field">
                  <b>Apellido</b>
                  <input
                    type="text"
                    required
                    autoComplete="family-name"
                    value={apellido}
                    onChange={e => setApellido(e.target.value)}
                    placeholder="Pérez"
                  />
                </label>
              </>
            )}
            <label className="lp-login-field">
              <b>Email</b>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="tu@email.com"
              />
            </label>

            {modo === 'signup' && (
              <label className="lp-login-field">
                <b>Confirmá tu email</b>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={emailConfirm}
                  onChange={e => setEmailConfirm(e.target.value)}
                  placeholder="tu@email.com"
                  onPaste={(e) => e.preventDefault()}
                />
              </label>
            )}

            {modo === 'login' && (
              <label className="lp-login-field">
                <b>Contraseña</b>
                <div className="lp-login-pw-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="lp-login-pw-toggle"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
              </label>
            )}

            {/* key={modo} → forza remount al cambiar signup<->login para regenerar token */}
            <TurnstileWidget
              key={modo}
              action={modo === 'signup' ? 'signup' : 'login'}
              onToken={setTurnstileToken}
            />

            {error && <div className="lp-login-err">{error}</div>}

            <button type="submit" disabled={loading} className="lp-btn-entrar">
              {loading
                ? (modo === 'signup' ? 'Enviando link…' : 'Entrando…')
                : (modo === 'signup' ? 'Continuar' : 'Entrar')}
            </button>
            {modo === 'signup' && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#64748B', textAlign: 'center', lineHeight: 1.5 }}>
                Te vamos a mandar un email con un link. Al abrirlo, elegís tu contraseña.
              </div>
            )}
          </form>

          {modo === 'login' && (
            <div className="lp-login-foot">
              <Link to="/reset-password">¿Olvidaste tu contraseña?</Link>
            </div>
          )}
          </>)}
        </div>

        <p className="lp-login-help">
          ¿Necesitás ayuda? Escribinos al{' '}
          <a href="https://wa.me/59898610001" target="_blank" rel="noopener">WhatsApp 098 610 001</a>
        </p>
      </main>

      <style>{styles}</style>
    </div>
  )
}

function traducirError(msg) {
  if (/invalid login credentials/i.test(msg)) return 'Email o contraseña incorrectos.'
  if (/email not confirmed/i.test(msg)) return 'Tenés que confirmar tu email primero. Revisá tu bandeja de entrada.'
  if (/user not found/i.test(msg)) return 'No encontramos una cuenta con ese email.'
  return msg
}

const styles = `
:root {
  --navy:  #1a3461;
  --blue:  #1a5cb8;
  --teal:  #00A8B5;
  --cream: #F8FAFC;
  --line2: #e2e8f0;
}
html, body { margin: 0; background: var(--navy) }
.lp-login {
  min-height: 100vh;
  position: relative;
  background: linear-gradient(135deg, #0d1f3d 0%, #1a3461 45%, #1a5cb8 100%);
  font-family: 'Inter', system-ui, sans-serif;
  color: var(--navy);
  -webkit-font-smoothing: antialiased;
  display: flex; flex-direction: column;
  overflow: hidden;
}
/* Halos turquesa decorativos (mismo lenguaje que el hero de la web) */
.lp-login::before, .lp-login::after {
  content: ''; position: absolute; pointer-events: none;
  border-radius: 50%; filter: blur(80px);
}
.lp-login::before {
  width: 420px; height: 420px;
  top: -120px; right: -120px;
  background: radial-gradient(circle, rgba(0,196,212,.45) 0%, rgba(0,196,212,0) 70%);
}
.lp-login::after {
  width: 380px; height: 380px;
  bottom: -100px; left: -120px;
  background: radial-gradient(circle, rgba(26,92,184,.55) 0%, rgba(26,92,184,0) 70%);
}

/* Navbar */
.lp-login-nav {
  position: relative; z-index: 2;
  background: rgba(13,31,61,.4);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255,255,255,.08);
}
.lp-login-nav-inner {
  max-width: 1100px; margin: 0 auto;
  padding: 16px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
}
.lp-login-logo {
  font-size: 22px; font-weight: 900;
  color: #fff; text-decoration: none;
  letter-spacing: -.02em;
}
.lp-login-logo span { color: var(--teal) }
.lp-login-nav-link {
  font-size: 13px; color: rgba(255,255,255,.65); text-decoration: none;
  transition: color .15s;
}
.lp-login-nav-link strong { color: var(--teal); font-weight: 700 }
.lp-login-nav-link:hover { color: #fff }

/* Main */
.lp-login-main {
  position: relative; z-index: 2;
  flex: 1;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 40px 16px;
  gap: 18px;
}

/* Card */
.lp-login-card {
  background: #fff;
  width: 100%; max-width: 420px;
  border-radius: 20px;
  padding: 36px 28px;
  box-shadow: 0 8px 40px rgba(26,52,97,.10);
  box-sizing: border-box;
}
@media (min-width: 480px) {
  .lp-login-card { padding: 40px 36px }
}

/* Isotipo Limpack (la gota) */
.lp-login-iso {
  width: 76px; height: 76px;
  margin: 0 auto 18px;
  display: flex; align-items: center; justify-content: center;
  background: #fff;
  border-radius: 22px;
  box-shadow: 0 8px 24px rgba(0,196,212,.2), 0 0 0 1px rgba(255,255,255,.6);
}
.lp-login-iso img {
  width: 56px; height: 56px;
  object-fit: contain;
  display: block;
}

/* Header */
.lp-login-head { text-align: center; margin-bottom: 24px }
.lp-login-head h1 {
  font-size: 24px; font-weight: 900;
  color: var(--navy); margin: 0 0 6px;
  letter-spacing: -.01em;
}
.lp-login-head p {
  font-size: 13px; color: #64748b; margin: 0;
}

/* Botón Google */
.lp-btn-google {
  width: 100%;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  background: #fff; color: var(--navy);
  border: 1.5px solid var(--line2);
  padding: 12px 16px;
  border-radius: 10px;
  font-weight: 600; font-size: 14px;
  cursor: pointer; font-family: inherit;
  transition: background .15s, border-color .15s;
  box-sizing: border-box;
}
.lp-btn-google:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}
.lp-btn-google:disabled { opacity: .6; cursor: wait }

/* Divider */
.lp-divider {
  display: flex; align-items: center; gap: 12px;
  color: #94a3b8; font-size: 12px;
  margin: 18px 0;
}
.lp-divider span { white-space: nowrap }
.lp-divider::before, .lp-divider::after {
  content: ''; flex: 1; height: 1px; background: var(--line2);
}

/* Form */
.lp-login-form { display: flex; flex-direction: column; gap: 14px }
.lp-login-field { display: block }
.lp-login-field b {
  display: block; font-size: 11px; font-weight: 800;
  color: var(--navy); letter-spacing: .08em;
  text-transform: uppercase; margin-bottom: 6px;
}
.lp-login-field input {
  width: 100%; box-sizing: border-box;
  padding: 12px 14px; font-size: 15px;
  border: 1.5px solid var(--line2); border-radius: 10px;
  background: #fff; color: var(--navy); font-family: inherit;
  transition: border-color .15s, box-shadow .15s;
}
.lp-login-field input:focus {
  outline: none;
  border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(0,196,212,.15);
}

/* Password toggle */
.lp-login-pw-wrap { position: relative }
.lp-login-pw-wrap input { padding-right: 44px }
.lp-login-pw-toggle {
  position: absolute; right: 8px; top: 50%;
  transform: translateY(-50%);
  background: none; border: 0;
  padding: 6px 8px; font-size: 16px;
  cursor: pointer; color: #94a3b8;
}
.lp-login-pw-toggle:hover { color: var(--blue) }

/* Error */
.lp-login-err {
  background: #fef2f2; border: 1px solid #fca5a5;
  color: #dc2626; border-radius: 10px;
  padding: 10px 14px; font-size: 13px;
}

/* Botón Entrar */
.lp-btn-entrar {
  width: 100%;
  background: linear-gradient(135deg, var(--blue) 0%, var(--teal) 100%);
  color: #fff; font-weight: 700; font-size: 15px;
  padding: 14px 20px; border: 0; border-radius: 10px;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 4px 14px rgba(0,196,212,.25);
  transition: transform .12s, box-shadow .12s, opacity .15s;
  margin-top: 4px;
}
.lp-btn-entrar:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0,196,212,.35);
}
.lp-btn-entrar:disabled { opacity: .6; cursor: wait }

/* Footer del card */
.lp-login-foot {
  margin-top: 22px; padding-top: 18px;
  border-top: 1px solid var(--line2);
  text-align: center;
}
.lp-login-foot a {
  color: #64748b; text-decoration: none;
  font-size: 13px; font-weight: 600;
  transition: color .15s;
}
.lp-login-foot a:hover { color: var(--blue); text-decoration: underline }

/* Help link debajo del card */
.lp-login-help {
  font-size: 12px; color: rgba(255,255,255,.55);
  text-align: center; margin: 0;
}
.lp-login-help a {
  color: var(--teal); text-decoration: none; font-weight: 600;
}
.lp-login-help a:hover { color: #fff; text-decoration: underline }
`
